export type AutosaveResult = { status: 'saved'; updatedAt: string } | { status: 'refused'; code: string; message: string } | { status: 'failed' };
export type AutosaveStatus = 'saved' | 'pending' | 'saving' | 'failed' | 'stale';

export class BlogDraftAutosave<Content> {
  private revision = 0;
  private savedRevision = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: Promise<boolean> | null = null;
  private stopped = false;
  private disposed = false;
  private savedKey: string | null = null;

  constructor(
    private updatedAt: string,
    private readonly read: () => Promise<Content | null>,
    private readonly save: (content: Content, updatedAt: string) => Promise<AutosaveResult>,
    private readonly status: (status: AutosaveStatus, result?: AutosaveResult) => void,
    private readonly saved: (content: Content) => void,
  ) {}

  changed() {
    this.revision++;
    if (this.stopped || this.disposed) return;
    this.clearTimer();
    this.status(this.pending ? 'saving' : 'pending');
    this.timer = setTimeout(() => void this.flush(), 2000);
  }

  hasChanges() {
    return this.revision !== this.savedRevision;
  }

  version() {
    return this.updatedAt;
  }

  flush(): Promise<boolean> {
    this.clearTimer();
    if (this.disposed || this.stopped) return Promise.resolve(false);
    if (this.pending) return this.pending;
    this.pending = this.drain().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  dispose() {
    this.disposed = true;
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private async drain(): Promise<boolean> {
    while (this.hasChanges() && !this.disposed) {
      const revision = this.revision;
      this.status('saving');
      let content: Content | null;
      let result: AutosaveResult;
      try {
        content = await this.read();
        if (content === null) throw new Error('Editor unavailable');
        const key = JSON.stringify(content);
        if (key === this.savedKey) {
          this.savedRevision = revision;
          continue;
        }
        result = await this.save(content, this.updatedAt);
        if (result.status === 'saved') {
          this.updatedAt = result.updatedAt;
          this.savedKey = key;
          this.savedRevision = revision;
          if (!this.disposed) this.saved(content);
          continue;
        }
      } catch {
        result = { status: 'failed' };
      }
      this.clearTimer();
      this.stopped = result.status === 'refused' && result.code === 'STALE';
      if (!this.disposed) this.status(this.stopped ? 'stale' : 'failed', result);
      return false;
    }
    if (!this.disposed) this.status('saved');
    return !this.disposed;
  }
}
